import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import { useNavigate, useParams } from "react-router";
import { useFormik } from "formik";
import * as Yup from "yup";
import { request } from "../../config/request.ts";
import { toast } from "react-toastify";
import PageHeader from "../../shared/PageHeader/index.tsx";
import TextField from "../../shared/TextField/index.tsx";
import SelectField from "../../shared/SelectField/index.tsx";
import AutoCompleteMultiSelect from "../../shared/AutoCompleteMultiSelect/index.tsx";
import FileUpload from "../../shared/FileUpload/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import "../../styles/scss/Form.scss";
import { imagepathReturn } from "../../config/formatters.ts";

export default function StaffDetailCreateEdit() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [editPhotoPreview, setEditPhotoPreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const isEdit = id && id !== "new";

  const formik = useFormik({
    initialValues: {
      employee_id: "",
      name: "",
      departments: [],
      educational_qualification: "",
      personal_qualification: "",
      photo: null,
      sort_order: "",
      isActive: "Active",
    },
    validationSchema: Yup.object({
      employee_id: Yup.string(),
      name: Yup.string().required("Name is required"),
      departments: Yup.array()
        .min(1, "At least one department is required")
        .required("Department is required"),
      sort_order: Yup.string().required("Sort Order is required"),
      photo: isEdit ? Yup.mixed().nullable() : Yup.mixed().required("Photo is required"),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("employee_id", values.employee_id);
      formData.append("name", values.name);
      values.departments.forEach((department: { id: number }, index: number) => {
        formData.append(`departments[${index}]`, department.id as any);
      });
      formData.append("educational_qualification", values.educational_qualification);
      formData.append("personal_qualification", values.personal_qualification);
      formData.append("sort_order", values.sort_order);
      formData.append("status", values?.isActive === "Active" ? "true" : "false");
      if (values.photo) {
        formData.append("photo", values.photo);
      }

      try {
        let response;
        if (isEdit) {
          formData.append("_method", "PUT");
          response = await request.post("staff-details/" + id, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } else {
          response = await request.post("staff-details/create", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        }

        if (response?.data?.status) {
          toast.success(
            isEdit ? "Staff updated successfully!" : "Staff created successfully!",
          );
          navigate("/admin/staff-details");
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred. Please try again.");
      }
    },
  });

  useEffect(() => {
    if (isEdit) {
      const fetchItem = async () => {
        setLoading(true);
        try {
          const response = await request.get(`staff-details/${id}`);
          const item = response.data.data;
          formik.setValues({
            employee_id: item.employee_id || "",
            name: item.name || "",
            departments: item.departments || [],
            educational_qualification: item.educational_qualification || "",
            personal_qualification: item.personal_qualification || "",
            photo: null,
            sort_order: item.sort_order?.toString() || "",
            isActive: item.status ? "Active" : "Inactive",
          });
          setEditPhotoPreview(imagepathReturn(item.photo || null));
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      };

      fetchItem();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <PageWrapper loading={loading}>
      <Box className="form-container">
        <Box className="form-page">
          <Box className="form-header">
            <PageHeader
              title={isEdit ? "Edit Staff" : "Create New Staff"}
              secondaryButton={{
                label: "Cancel",
                onClick: () => navigate("/admin/staff-details"),
              }}
              primaryButton={{
                label: isEdit ? "Update Staff" : "Create Staff",
                icon: <span className="material-symbols-outlined">save</span>,
                onClick: () => formik.handleSubmit(),
              }}
            />
          </Box>

          <Box className="form-card">
            <Box className="form-card-body">
              <Box className="form-grid form-grid--2">
                <Box className="form-field">
                  <TextField name="employee_id" label="Employee ID" type="text" formik={formik} autoFocus />
                </Box>
                <Box className="form-field">
                  <TextField name="name" label="Name" type="text" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <AutoCompleteMultiSelect
                    key={`departments-${id || "new"}`}
                    name="departments"
                    label="Departments"
                    formik={formik}
                    apiUrl="departments"
                    optionLabelKey="title"
                    optionValueKey="id"
                  />
                </Box>
                <Box className="form-field">
                  <TextField name="sort_order" label="Sort Order" type="number" formik={formik} />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <TextField
                    name="educational_qualification"
                    label="Educational Qualification"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
                <Box className="form-field">
                  <TextField
                    name="personal_qualification"
                    label="Personal Qualification"
                    type="text"
                    multiline
                    minRows={3}
                    formik={formik}
                  />
                </Box>
              </Box>

              <Box className="form-grid form-grid--2" style={{ marginTop: "16px" }}>
                <Box className="form-field">
                  <SelectField
                    name="isActive"
                    label="Status"
                    formik={formik}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ]}
                  />
                </Box>
                <Box className="form-field">
                  <FileUpload
                    name="photo"
                    formik={formik}
                    label="Upload Photo"
                    editImagePreview={editPhotoPreview}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </PageWrapper>
  );
}